export function roundRating(v: number): number {
  return Math.round(v * 10) / 10;
}

/** Текст изменения рейтинга для HUD: «+0.1», «-0.2». */
export function formatSignedRatingDelta(delta: number): string {
  const rounded = roundRating(delta);
  if (rounded > 0) {
    return `+${rounded.toFixed(1)}`;
  }
  return rounded.toFixed(1);
}

/** Средний рейтинг: сумма лучших оценок / число доставок с оценкой. */
export function computeAverageRating(ratingSum: number, ratedDeliveriesCount: number): number {
  if (ratedDeliveriesCount <= 0) {
    return 0;
  }
  return roundRating(ratingSum / ratedDeliveriesCount);
}

export type RatingAggregateUpdate = {
  ratingSum: number;
  ratedDeliveriesCount: number;
  totalRating: number;
  ratingDelta: number;
};

export function applyDeliveryRatingToAggregate(
  ratingSum: number,
  ratedDeliveriesCount: number,
  previousBestRating: number,
  newBestRating: number,
): RatingAggregateUpdate {
  const oldAverage = computeAverageRating(ratingSum, ratedDeliveriesCount);
  let nextSum = ratingSum;
  let nextCount = ratedDeliveriesCount;
  if (previousBestRating === 0) {
    nextSum += newBestRating;
    nextCount += 1;
  } else {
    nextSum += newBestRating - previousBestRating;
  }
  const totalRating = computeAverageRating(nextSum, nextCount);
  return {
    ratingSum: nextSum,
    ratedDeliveriesCount: nextCount,
    totalRating,
    ratingDelta: roundRating(totalRating - oldAverage),
  };
}

/** Миграция старых сохранений без ratingSum / ratedDeliveriesCount. */
export function migrateRatingFields(
  data: {
    totalRating?: number;
    ratingSum?: number;
    ratedDeliveriesCount?: number;
    deliveryPool?: Array<{ bestRating?: number }>;
  },
): { ratingSum: number; ratedDeliveriesCount: number; totalRating: number } {
  if (
    typeof data.ratingSum === 'number' &&
    Number.isFinite(data.ratingSum) &&
    typeof data.ratedDeliveriesCount === 'number' &&
    Number.isInteger(data.ratedDeliveriesCount) &&
    data.ratedDeliveriesCount >= 0
  ) {
    return {
      ratingSum: data.ratingSum,
      ratedDeliveriesCount: data.ratedDeliveriesCount,
      totalRating: computeAverageRating(data.ratingSum, data.ratedDeliveriesCount),
    };
  }

  const rated = (data.deliveryPool ?? []).filter((e) => (e.bestRating ?? 0) > 0);
  const ratingSum = rated.reduce((sum, e) => sum + (e.bestRating ?? 0), 0);
  const ratedDeliveriesCount = rated.length;
  return {
    ratingSum,
    ratedDeliveriesCount,
    totalRating: computeAverageRating(ratingSum, ratedDeliveriesCount),
  };
}
